import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, VerifyData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Add, Edit, Visibility, Delete, ShoppingCart, FilterList, Refresh, Warning, CheckCircle, Cancel, Close
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Grid
} from '@mui/material';
import SaleOrderModal from './SaleOrderModal';
import './SaleOrderIndex.css';

const SaleOrderIndex = () => {
  // Session validation
  useSessionValidation();
  
  const { companyId, branchId, token, userId } = useAuth();
  const { canCreateTransaction, canEditTransaction, canDeleteTransaction } = usePermissions();

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [SaleOrders, setSaleOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());
  
  const [fromDate, setFromDate] = useState(getCurrentDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [SaleOrderDetails, setSaleOrderDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');

  // Details section state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData(fromDate, toDate);
    }
  }, [companyId, token, fromDate, toDate]);

  // Force re-render when SaleOrders change
  const [renderKey, setRenderKey] = useState(0);
  
  useEffect(() => {
    console.log('SaleOrders state changed:', SaleOrders);
    setRenderKey(prev => prev + 1);
  }, [SaleOrders]);

  // Fetch functions
  const fetchSaleOrders = async (fromDateParam = '', toDateParam = '') => {
    try {
      let endpoint = `/${companyId}/${branchId}/`;
      
      // Add date range parameters if provided
      if (fromDateParam && toDateParam) {
        endpoint += `${fromDateParam}/${toDateParam}`;
      } else if (fromDateParam) {
        endpoint += `${fromDateParam}`;
      } else if (toDateParam) {
        endpoint += `/${toDateParam}`;
      }
      
      console.log('Fetching Sale orders from endpoint:', endpoint);
      const response = await fetchData('SaleOrderApi', endpoint, token);
      console.log('Raw API response:', response);
      
      // Handle different response structures
      let SaleOrderData = [];
      if (response) {
        if (response.response_data) {
          SaleOrderData = Array.isArray(response.response_data) ? response.response_data : [];
        } else if (Array.isArray(response)) {
          SaleOrderData = response;
        } else if (response.data) {
          SaleOrderData = Array.isArray(response.data) ? response.data : [];
        } else if (response.SaleOrders) {
          SaleOrderData = Array.isArray(response.SaleOrders) ? response.SaleOrders : [];
        }
      }
      
      console.log('Processed Sale order data:', SaleOrderData);
      console.log('Number of Sale orders:', SaleOrderData.length);
      
      // Force state update with callback to ensure re-render
      setSaleOrders(prevSaleOrders => {
        console.log('Previous Sale orders:', prevSaleOrders);
        console.log('Setting new Sale orders:', SaleOrderData);
        return [...SaleOrderData]; // Create new array reference
      });
    } catch (error) {
      console.error('Error fetching Sale orders:', error);
      setSaleOrders([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      setCustomers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('SaleOrderApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      console.log('MaxBillNo API Response:', response);
      const data = response.response_data || response || {};
      console.log('MaxBillNo Data:', data);
      
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || 'SO';
      const finYear = data.FinYear || data.finYear || '';
      
      // Generate SaleOrderNo based on available data
      let SaleOrderNo = '';
      if (billPrefix && finYear) {
        SaleOrderNo = `${billPrefix}/${billNumber}/${finYear}`;
      } else if (finYear) {
        SaleOrderNo = `${billNumber}/${finYear}`;
      } else if (billPrefix) {
        SaleOrderNo = `${billPrefix}/${billNumber}`;
      } else {
        SaleOrderNo = `${billNumber}`;
      }
      
      return { billNumber, billPrefix, finYear, SaleOrderNo };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: 'SO', finYear: '', SaleOrderNo: 'SO/1' };
    }
  };

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  // Freight + Packing GST
  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('SaleOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      console.log('AccountHeadFreightAndPacking API Response:', response);
      const data = response.response_data || response || [];
      console.log('AccountHeadFreightAndPacking Data:', data);
      
      // Find Freight and Packing entries from the response
      let freightData = null;
      let packingData = null;
      
      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight') || item.AccountHeadName?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing') || item.AccountHeadName?.toLowerCase().includes('packing'));
      } else if (data) {
        // If it's an object with Freight and Packing properties
        freightData = data.Freight || data.FreightCharge || null;
        packingData = data.Packing || data.PackingCharge || null;
      }
      
      setFreightPackingGST({
        freight: freightData,
        packing: packingData
      });
      
      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };

  const fetchAllData = async (fromDateParam = '', toDateParam = '') => {
    setLoading(true);
    setLoadingMessage('Fetching Sale orders...');
    await Promise.allSettled([
      fetchSaleOrders(fromDateParam, toDateParam), 
      fetchCustomers(), 
      fetchItems(), 
      fetchFreightAndPackingGST()
    ]);
    setLoading(false);
  };

  const fetchSingleSaleOrder = async (id) => {
    try {
      const response = await fetchData('SaleOrderApi', `/${id}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching Sale order details:', error);
      return null;
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);

      const freshData = await fetchSingleSaleOrder(itemId);

      if (freshData) {
        const SaleOrderData = freshData.SaleOrder || freshData;
        const details = freshData.SaleOrderDetails || freshData.Details || [];
        
        setSelectedItem(SaleOrderData);
        setFormData({
          Id: SaleOrderData.Id,
          CompanyId: SaleOrderData.CompanyId || companyId,
          BranchId: SaleOrderData.BranchId || 0,
          MasterId: SaleOrderData.MasterId,
          InvoiceTypeId: SaleOrderData.InvoiceTypeId || 0,
          SaleOrderNo: SaleOrderData.SaleOrderNo,
          SaleOrderNumber: SaleOrderData.SaleOrderNumber || 0,
          SaleOrderPrefix: SaleOrderData.SaleOrderPrefix || '',
          SaleOrderDate: SaleOrderData.SaleOrderDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          FinYear: SaleOrderData.FinYear || '',
          Instruction1: SaleOrderData.Instruction1 || '',
          Instruction2: SaleOrderData.Instruction2 || '',
          Remarks: SaleOrderData.Remarks || '',
          Amount: SaleOrderData.Amount || 0,
          DiscountAmount: SaleOrderData.DiscountAmount || 0,
          TaxableAmount: SaleOrderData.TaxableAmount || 0,
          CGSTAmount: SaleOrderData.CGSTAmount || 0,
          SGSTAmount: SaleOrderData.SGSTAmount || 0,
          IGSTAmount: SaleOrderData.IGSTAmount || 0,
          FreightAmount: SaleOrderData.FreightAmount || 0,
          FreightGSTRate: SaleOrderData.FreightGSTRate || 0,
          FreightGSTAmount: SaleOrderData.FreightGSTAmount || 0,
          PackingAmount: SaleOrderData.PackingAmount || 0,
          PackingGSTRate: SaleOrderData.PackingGSTRate || 0,
          PackingGSTAmount: SaleOrderData.PackingGSTAmount || 0,
          RoundOff: SaleOrderData.RoundOff || 0,
          TotalAmount: SaleOrderData.TotalAmount || 0,
          IsConverted: SaleOrderData.IsConverted || false,
          IsVerified: SaleOrderData.IsVerified || false,
          IsActive: SaleOrderData.IsActive ?? true
        });
        setSaleOrderDetails(details.map(d => ({
          Id: d.Id || 0,
          ItemId: d.ItemId,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          IsActive: d.IsActive ?? true
        })));
      } else {
        const item = typeof itemOrId === 'object' ? itemOrId : null;
        setSelectedItem(item);
        setFormData(item || {});
        setSaleOrderDetails([]);
      }

      setModalLoading(false);
    } else {
      // Create mode - fetch max bill number and freight/packing GST
      setModalLoading(true);
      
      const maxBillData = await fetchMaxBillNo();
      const gstData = await fetchFreightAndPackingGST();
      
      // Get GST rates from API data
      const freightGSTRate = gstData.freight?.GSTRate || gstData.freight?.GstRate || 0;
      const packingGSTRate = gstData.packing?.GSTRate || gstData.packing?.GstRate || 0;
      
      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        MasterId: '',
        InvoiceTypeId: 0,
        SaleOrderNumber: maxBillData.billNumber,
        SaleOrderNo: maxBillData.SaleOrderNo,
        SaleOrderPrefix: maxBillData.billPrefix,
        SaleOrderDate: new Date().toISOString().split('T')[0],
        FinYear: maxBillData.finYear,
        Instruction1: '',
        Instruction2: '',
        Remarks: '',
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        FreightAmount: 0,
        FreightGSTRate: freightGSTRate,
        FreightGSTAmount: 0,
        PackingAmount: 0,
        PackingGSTRate: packingGSTRate,
        PackingGSTAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        IsConverted: false,
        IsVerified: false,
        IsActive: true
      });
      setSaleOrderDetails([]);
      setModalLoading(false);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setSaleOrderDetails([]);
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} Sale order...`);

      const dataToSend = {
        SaleOrder: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || 0,
          MasterId: parseInt(formData.MasterId) || 0,
          InvoiceTypeId: formData.InvoiceTypeId || 0,
          SaleOrderNo: formData.SaleOrderNo || '',
          SaleOrderNumber: formData.SaleOrderNumber || 0,
          SaleOrderPrefix: formData.SaleOrderPrefix || '',
          SaleOrderDate: formData.SaleOrderDate || new Date().toISOString().split('T')[0],
          FinYear: formData.FinYear || '',
          Instruction1: formData.Instruction1 || '',
          Instruction2: formData.Instruction2 || '',
          Remarks: formData.Remarks || '',
          Amount: parseFloat(formData.Amount) || 0,
          DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(formData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(formData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(formData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(formData.IGSTAmount) || 0,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(formData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(formData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(formData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(formData.PackingGSTAmount) || 0,
          RoundOff: parseFloat(formData.RoundOff) || 0,
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          IsConverted: formData.IsConverted || false,
          IsVerified: formData.IsVerified || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        SaleOrderDetails: SaleOrderDetails.map(detail => ({
          Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
          CompanyId: companyId,
          SaleOrderId: modalAction === 'edit' ? formData.Id : 0,
          ItemId: parseInt(detail.ItemId) || 0,
          HSNCode: detail.HSNCode || '',
          GSTRate: parseFloat(detail.GSTRate) || 0,
          Quantity: parseFloat(detail.Quantity) || 0,
          Rate: parseFloat(detail.Rate) || 0,
          Amount: parseFloat(detail.Amount) || 0,
          DiscountPercentage: parseFloat(detail.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(detail.DiscountAmount) || 0,
          TaxableAmount: parseFloat(detail.TaxableAmount) || 0,
          CGSTAmount: parseFloat(detail.CGSTAmount) || 0,
          SGSTAmount: parseFloat(detail.SGSTAmount) || 0,
          IGSTAmount: parseFloat(detail.IGSTAmount) || 0,
          TotalAmount: parseFloat(detail.TotalAmount) || 0,
          IsActive: detail.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
          ModifiedBy: modalAction === 'edit' ? userId : null
        }))
      };

      if (modalAction === 'create') {
        await postData('SaleOrderApi', '', dataToSend, token);
        setSuccess('Sale order created successfully!');
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Sale Order ID is missing');
        await updateData('SaleOrderApi', formData.Id, dataToSend, token);
        setSuccess('Sale order updated successfully!');
      }

      handleCloseModal();
      await fetchSaleOrders(fromDate, toDate);
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} Sale order. Please try again.`);
      setLoading(false);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, SaleOrderNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(SaleOrderNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('SaleOrderApi', deleteItemId, token);
      setSuccess('Sale order deleted successfully!');
      await fetchSaleOrders(fromDate, toDate);
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete Sale order.');
    }
    setLoading(false);
  };

  // Details section handlers
  const handleOpenDetails = async (item) => {
    setModalLoading(true);
    const freshData = await fetchSingleSaleOrder(item.Id);
    
    if (freshData) {
      const SaleOrderData = freshData.SaleOrder || freshData;
      const details = freshData.SaleOrderDetails || freshData.Details || [];
      
      setSelectedDetails({
        ...SaleOrderData,
        Details: details,
        CustomerName: item.CustomerName || customers.find(c => c.MasterId === SaleOrderData.MasterId)?.Name || '-'
      });
      setDetailsOpen(true);
    }
    setModalLoading(false);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedDetails(null);
  };

  // Verify handler
  const handleVerifySaleOrder = async (SaleOrderId, SaleOrderNo) => {
    if (!SaleOrderId) return;

    setLoading(true);
    setLoadingMessage('Verifying Sale order...');
    
    try {
      const response = await VerifyData('SaleOrderApi', SaleOrderId, token);
      
      if (response && response.success !== false) {
        setSuccess(`Sale order ${SaleOrderNo} verified successfully!`);
        // Refresh the Sale orders list to show updated verification status
        await fetchSaleOrders(fromDate, toDate);
        // Close the modal after successful verification
        handleCloseModal();
      } else {
        throw new Error(response?.message || 'Verification failed');
      }
    } catch (error) {
      console.error('Error verifying Sale order:', error);
      setError(`Failed to verify Sale order ${SaleOrderNo}. ${error.message || 'Please try again.'}`);
    }
    
    setLoading(false);
  };

  // Filter and pagination
  const getFilteredData = (data) => {
    console.log('getFilteredData input:', data);
    
    if (!Array.isArray(data)) {
      console.warn('getFilteredData received non-array data:', typeof data, data);
      return [];
    }
    
    let filtered = [...data]; // Create a copy to avoid mutations

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.SaleOrderNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customers.find(c => c.MasterId === item.MasterId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item =>
        filterStatus === 'active' ? item.IsActive : !item.IsActive
      );
    }

    if (filterVerified !== 'all') {
      filtered = filtered.filter(item =>
        filterVerified === 'verified' ? item.IsVerified : !item.IsVerified
      );
    }

    console.log('getFilteredData output:', filtered);
    return filtered;
  };

  const getPaginatedData = (data) => {
    console.log('getPaginatedData input:', data);
    
    const filtered = getFilteredData(data) || [];
    console.log('getPaginatedData filtered:', filtered);
    
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    
    const paginatedResult = {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
    
    console.log('getPaginatedData result:', paginatedResult);
    return paginatedResult;
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  const handleRefresh = () => fetchAllData(fromDate, toDate);
  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  // Date change handlers
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(SaleOrders);

  return (
    <div className="Saleorder-dashboard">
      <div className="dashboard-header">
        <h5 className="plain-header">
          Sale Order Management
        </h5>
      </div>

      <div className="dashboard-content">
        <div className="data-table-container">
          <div className="table-header">
            <div className="search-filter-section">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by Sale Order No, Customer..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>
              
              {/* Date Range Filter */}
              <div className="date-filter-section">
                <input
                  type="date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  className="date-input"
                  title="From Date"
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={handleToDateChange}
                  className="date-input"
                  title="To Date"
                />
              </div>
              
              <div className="filter-section">
                <FilterList className="filter-icon" />
                <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="filter-section">
                <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              {/* Desktop buttons */}
              <Button variant="outlined" onClick={handleRefresh} disabled={loading} className="refresh-button desktop-only" startIcon={<Refresh />}>
                Refresh
              </Button>
              {canCreateTransaction && (
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal('create')} className="add-button desktop-only">
                  Add Sale Order
                </Button>
              )}
              {/* Mobile icon buttons */}
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canCreateTransaction && (
                <Tooltip title="Add Sale Order">
                  <IconButton onClick={() => handleOpenModal('create')} className="add-icon-btn mobile-only">
                    <Add />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>

          <div className="table-wrapper">
            <table className="data-table" key={`Saleorder-table-${renderKey}`}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center' }}>Sl No.</th>
                  <th>Sale Order No.</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th style={{ textAlign: 'right' }}>Total Amount</th>
                  <th>Verified</th>
                  <th>Converted</th>
                  <th style={{ textAlign: 'right' }}>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(paginatedData) && paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <tr key={item.Id || index}>
                      <td style={{ textAlign: 'center' }}>
                        <span onClick={() => handleOpenDetails(item)} style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                          {startIndex + index + 1}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => handleOpenDetails(item)}>
                          <ShoppingCart sx={{ fontSize: 18, color: '#1e40af' }} />
                          {item.SaleOrderNo || '-'}
                        </div>
                      </td>
                      <td>{formatDate(item.SaleOrderDate)}</td>
                      <td>{item.CustomerName || customers.find(c => c.MasterId === item.MasterId)?.Name || '-'}</td>
                      <td style={{ fontWeight: 700, textAlign: 'right' }}>{formatCurrency(item.TotalAmount)}</td>
                      <td>
                        {item.IsVerified ? (
                          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
                        ) : (
                          <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Pending" color="warning" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
                        )}
                      </td>
                      <td>
                        {item.IsConverted ? (
                          <Chip label="Converted" color="info" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        ) : (
                          <Chip label="Open" color="default" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Chip
                          label={item.IsActive ? 'Active' : 'Inactive'}
                          color={item.IsActive ? 'success' : 'default'}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: '20px' }}
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="action-buttons">
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => handleOpenModal('view', item.Id)} className="view-btn">
                              <Visibility style={{ color: 'white' }} />
                            </IconButton>
                          </Tooltip>
                          {canEditTransaction && (
                            <Tooltip title={item.IsVerified ? "Cannot edit verified order" : "Edit"}>
                              <span>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleOpenModal('edit', item.Id)} 
                                  disabled={item.IsConverted || item.IsVerified} 
                                  className="edit-btn"
                                >
                                  <Edit style={{ color: 'white' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {canDeleteTransaction && (
                            <Tooltip title={item.IsVerified ? "Cannot delete verified order" : "Delete"}>
                              <span>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleOpenDeleteModal(item.Id, item.SaleOrderNo)} 
                                  disabled={item.IsConverted || item.IsVerified} 
                                  className="delete-btn"
                                >
                                  <Delete style={{ color: 'white' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                      {loading ? 'Loading Sale orders...' : 
                       Array.isArray(SaleOrders) ? 
                         (SaleOrders.length === 0 ? 'No Sale orders found' : 'No Sale orders match current filters') :
                         'Error loading Sale orders - invalid data format'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-section">
            <div className="pagination-info">
              <span>Showing {totalCount > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + rowsPerPage, totalCount)} of {totalCount} entries</span>
              <div className="rows-per-page">
                <span>Rows per page:</span>
                <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
            <div className="pagination-controls">
              <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</button>
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Prev</button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <button key={page} className={`pagination-btn ${currentPage === page ? 'active' : ''}`} onClick={() => handlePageChange(page)}>
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="pagination-ellipsis">...</span>;
                }
                return null;
              })}
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>Next</button>
              <button className="pagination-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0}>Last</button>
            </div>
          </div>
        </div>
      </div>

      <LoadingOverlay open={loading} message={loadingMessage} />

      <SaleOrderModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        SaleOrderDetails={SaleOrderDetails}
        setSaleOrderDetails={setSaleOrderDetails}
        onSave={handleSave}
        onVerify={handleVerifySaleOrder}
        customers={customers}
        items={items}
        loading={loading}
        freightPackingGST={freightPackingGST}
      />

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success">{success}</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error">{error}</Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef2f2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Delete Sale Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete Sale order <strong style={{ color: '#1e293b' }}>{deleteItemNo}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sale Order Details Section */}
      <Dialog 
        open={detailsOpen} 
        onClose={handleCloseDetails} 
        maxWidth="md" 
        fullWidth 
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ backgroundColor: '#dbeafe', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShoppingCart sx={{ color: '#1e40af', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Sale Order Details
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {selectedDetails?.SaleOrderNo || '-'}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseDetails} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {selectedDetails && (
            <Box>
              {/* Header Information */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Customer</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{selectedDetails.CustomerName}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{formatDate(selectedDetails.SaleOrderDate)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Status</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {selectedDetails.IsVerified ? (
                        <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
                      ) : (
                        <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Pending" color="warning" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Items Table */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 1.5 }}>Order Items</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Item</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#64748b' }}>Qty</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Rate</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#64748b' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDetails.Details && selectedDetails.Details.length > 0 ? (
                      selectedDetails.Details.map((detail, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {items.find(i => i.Id === detail.ItemId)?.Name || `Item ${detail.ItemId}`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              HSN: {detail.HSNCode || '-'} | GST: {detail.GSTRate}%
                            </Typography>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>{detail.Quantity}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(detail.Rate)}</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(detail.TotalAmount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>No items found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>

              {/* Summary */}
              <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Taxable Amount:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(selectedDetails.TaxableAmount)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>GST Amount:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(selectedDetails.CGSTAmount + selectedDetails.SGSTAmount + selectedDetails.IGSTAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Freight + Packing:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency((selectedDetails.FreightAmount || 0) + (selectedDetails.PackingAmount || 0) + (selectedDetails.FreightGSTAmount || 0) + (selectedDetails.PackingGSTAmount || 0))}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ borderTop: '2px solid #e2e8f0', mt: 1, pt: 1 }}>
                      <Grid container>
                        <Grid item xs={6}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e40af' }}>Grand Total:</Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ textAlign: 'right' }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e40af' }}>{formatCurrency(selectedDetails.TotalAmount)}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {selectedDetails.Remarks && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#fffbeb', borderRadius: 2, border: '1px solid #fef3c7' }}>
                  <Typography variant="caption" sx={{ color: '#92400e', display: 'block', mb: 0.5, fontWeight: 600 }}>Remarks:</Typography>
                  <Typography variant="body2" sx={{ color: '#78350f' }}>{selectedDetails.Remarks}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button 
            onClick={handleCloseDetails} 
            variant="outlined" 
            sx={{ 
              borderColor: '#e2e8f0', 
              color: '#64748b', 
              '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } 
            }}
          >
            Close
          </Button>
          
          {selectedDetails && !selectedDetails.IsVerified && (
            <Button 
              onClick={() => {
                handleVerifySaleOrder(selectedDetails.Id, selectedDetails.SaleOrderNo);
                handleCloseDetails();
              }}
              variant="contained" 
              startIcon={<CheckCircle />}
              sx={{ 
                background: 'linear-gradient(135deg, #16a34a, #15803d)', 
                color: '#fff',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #15803d, #166534)'
                }
              }}
            >
              Verify Sale Order
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal Loading Spinner */}
      {modalLoading && <LoadingOverlay message="Loading..." />}
    </div>
  );
};

export default SaleOrderIndex;
